import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Settings, Edit, Key, QrCode, CheckCircle, XCircle, Smartphone, UserX } from 'lucide-react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Separator } from '../ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const ROLE_COLORS = {
  ADMIN_IT:   'bg-red-600',
  RH:         'bg-blue-600',
  TEAM_LEAD:  'bg-orange-600',
  EMPLOYE:    'bg-gray-600',
  SUPERVISEUR:'bg-purple-600',
  AUDITEUR:   'bg-green-600',
};

const getRoleBadge = (roleName) => (
  <Badge variant="default" className={ROLE_COLORS[roleName] || 'bg-gray-600'}>{roleName}</Badge>
);

const SecurityUsersAccordionItem = ({ users, totalUsers, toggleUserStatus, resetPassword, setUserDialog, setTwoFactorDialog }) => {
  const { t } = useTranslation();

  return (
    <AccordionItem value="users">
      <AccordionTrigger>
        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4" />
          <span>{t('security.users.sectionTitle')}</span>
          <Badge variant="secondary">{totalUsers}</Badge>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <Card>
          <CardContent className="p-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('security.users.user')}</TableHead>
                  <TableHead>{t('security.users.roles')}</TableHead>
                  <TableHead>{t('security.users.status')}</TableHead>
                  <TableHead>{t('security.users.twoFA')}</TableHead>
                  <TableHead>{t('security.users.lastLogin')}</TableHead>
                  <TableHead>{t('security.users.failures')}</TableHead>
                  <TableHead className="text-right">{t('security.users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {user.prenom?.charAt(0)}{user.nom?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.prenom} {user.nom}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => getRoleBadge(role))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant={user.statut === 'ACTIF' ? 'default' : 'secondary'}
                        size="sm"
                        className={user.statut === 'ACTIF' ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => toggleUserStatus(user.id, user.statut)}
                      >
                        {user.statut === 'ACTIF' ? (
                          <><CheckCircle className="h-3 w-3 mr-1" />{t('security.users.active')}</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" />{t('security.users.inactive')}</>
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {user.otp_enabled ? (
                        <Badge variant="default" className="bg-green-600">
                          <Smartphone className="h-3 w-3 mr-1" />{t('security.users.enabled')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">{t('security.users.disabled')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {user.derniere_connexion
                          ? new Date(user.derniere_connexion).toLocaleString()
                          : t('security.users.never')}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.tentatives_connexion_echouees > 0 ? (
                        <Badge variant="destructive">{user.tentatives_connexion_echouees}</Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                          <div className="space-y-2">
                            <Button variant="ghost" size="sm" className="w-full justify-start"
                              onClick={() => setUserDialog({ open: true, user, mode: 'edit' })}>
                              <Edit className="mr-2 h-4 w-4" />{t('security.users.edit')}
                            </Button>
                            <Button variant="ghost" size="sm" className="w-full justify-start"
                              onClick={() => resetPassword(user.id)}>
                              <Key className="mr-2 h-4 w-4" />{t('security.users.resetPassword')}
                            </Button>
                            <Button variant="ghost" size="sm" className="w-full justify-start"
                              onClick={() => setTwoFactorDialog({ open: true, user })}>
                              <QrCode className="mr-2 h-4 w-4" />{t('security.users.configure2FA')}
                            </Button>
                            <Separator />
                            {user.statut === 'ACTIF' ? (
                              <Button variant="ghost" size="sm" className="w-full justify-start text-orange-600"
                                onClick={() => toggleUserStatus(user.id, 'SUSPENDU')}>
                                <UserX className="mr-2 h-4 w-4" />{t('security.users.suspend')}
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" className="w-full justify-start text-green-600"
                                onClick={() => toggleUserStatus(user.id, 'ACTIF')}>
                                <CheckCircle className="mr-2 h-4 w-4" />{t('security.users.activate')}
                              </Button>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </AccordionContent>
    </AccordionItem>
  );
};

export default SecurityUsersAccordionItem;
